/**
 * Lightweight step-by-step guided tour with spotlight + tooltip.
 *
 * Usage:
 *   1. Add `data-tour-id="opening-float"` to any element you want to spotlight.
 *   2. Render <GuideTour storageKey="cash-register-guide-v1" steps={[...]} />.
 *      When `storageKey` hasn't been dismissed yet the tour auto-starts on mount.
 *   3. Optionally expose a "Show me around" button by passing an `openRef` and
 *      calling `openRef.current?.open()`.
 *
 * Steps: [{ target: "tour-id", title, body, placement?: "top"|"bottom"|"auto" }]
 *
 * Design notes:
 *   - No third-party library. Pure DOM measurement + Tailwind.
 *   - Recomputes on window resize / scroll so the callout stays glued.
 *   - Falls back to a centred modal when the target isn't on-screen.
 */
import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, forwardRef } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const PADDING = 8;                // spotlight halo padding around the target
const CALLOUT_WIDTH = 340;        // px
const GAP = 14;                   // gap between spotlight and callout

function measure(target) {
    if (!target) return null;
    const el = document.querySelector(`[data-tour-id="${target}"]`);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null;
    return {
        el,
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        bottom: r.bottom,
        right: r.right,
    };
}

const GuideTour = forwardRef(function GuideTour(
    { storageKey, steps, autoStart = true, onFinish },
    ref,
) {
    const [open, setOpen] = useState(false);
    const [idx, setIdx] = useState(0);
    const [rect, setRect] = useState(null);
    const [tick, setTick] = useState(0);      // forces re-measure

    // Public API for parent to trigger the tour manually.
    useImperativeHandle(ref, () => ({
        open: () => { setIdx(0); setOpen(true); },
        close: () => setOpen(false),
    }));

    // Auto-start once per storageKey.
    useEffect(() => {
        if (!autoStart || !storageKey) return;
        try {
            const seen = localStorage.getItem(storageKey);
            if (!seen) setOpen(true);
        } catch { /* ignore */ }
    }, [autoStart, storageKey]);

    // Re-measure when step changes / window resizes / a scroll happens.
    useLayoutEffect(() => {
        if (!open) return;
        const step = steps[idx];
        const r = measure(step?.target);
        setRect(r);
        if (r?.el) {
            r.el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, [open, idx, tick, steps]);

    useEffect(() => {
        if (!open) return;
        const bump = () => setTick((t) => t + 1);
        window.addEventListener("resize", bump);
        window.addEventListener("scroll", bump, true);
        // Also poll for the first 1.5s in case the target renders slightly late.
        const iv = setInterval(bump, 300);
        const stop = setTimeout(() => clearInterval(iv), 1500);
        return () => {
            window.removeEventListener("resize", bump);
            window.removeEventListener("scroll", bump, true);
            clearInterval(iv);
            clearTimeout(stop);
        };
    }, [open, idx]);

    if (!open) return null;

    const step = steps[idx] || {};
    const isFirst = idx === 0;
    const isLast = idx === steps.length - 1;

    function finish(remember = true) {
        setOpen(false);
        if (remember && storageKey) {
            try { localStorage.setItem(storageKey, new Date().toISOString()); } catch { /* ignore */ }
        }
        onFinish?.();
    }

    // Spotlight box in viewport coords (or centre if no target).
    const hasTarget = !!rect;
    const box = hasTarget
        ? {
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
        }
        : null;

    // Compute callout position. Prefer BELOW the target; fall back to ABOVE
    // if there isn't room. When no target, centre the callout.
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
    let callout;
    if (!hasTarget) {
        callout = {
            top: Math.max(24, (vh - 260) / 2),
            left: Math.max(16, (vw - CALLOUT_WIDTH) / 2),
        };
    } else {
        const roomBelow = vh - rect.bottom;
        const preferBelow = (step.placement === "bottom") ||
            (step.placement !== "top" && roomBelow > 200);
        const top = preferBelow
            ? rect.bottom + GAP
            : Math.max(16, rect.top - GAP - 220);
        let left = rect.left + rect.width / 2 - CALLOUT_WIDTH / 2;
        left = Math.max(12, Math.min(left, vw - CALLOUT_WIDTH - 12));
        callout = { top, left };
    }

    return (
        <div className="fixed inset-0 z-[100]" data-testid="guide-tour" aria-live="polite">
            {/* Dimmer with SVG mask that cuts out the spotlight rectangle */}
            <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={() => finish(true)}>
                <defs>
                    <mask id="guide-tour-mask">
                        <rect x="0" y="0" width="100%" height="100%" fill="white" />
                        {box && (
                            <rect
                                x={box.left}
                                y={box.top}
                                width={box.width}
                                height={box.height}
                                rx="12"
                                ry="12"
                                fill="black"
                            />
                        )}
                    </mask>
                </defs>
                <rect x="0" y="0" width="100%" height="100%" fill="rgba(15,17,21,0.55)" mask="url(#guide-tour-mask)" />
                {/* Spotlight ring for extra affordance */}
                {box && (
                    <rect
                        x={box.left}
                        y={box.top}
                        width={box.width}
                        height={box.height}
                        rx="12"
                        ry="12"
                        fill="none"
                        stroke="rgba(16,185,129,0.9)"
                        strokeWidth="2"
                        style={{ pointerEvents: "none" }}
                    />
                )}
            </svg>

            {/* Callout card */}
            <div
                className="absolute rounded-2xl bg-white shadow-2xl border border-[#E4E4E7] p-4"
                style={{ top: callout.top, left: callout.left, width: CALLOUT_WIDTH }}
                onClick={(e) => e.stopPropagation()}
                data-testid="guide-tour-callout"
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="grid h-7 w-7 place-items-center rounded-full bg-emerald-100 text-emerald-700">
                            <Sparkles className="h-3.5 w-3.5" />
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5E636E]">
                            Step {idx + 1} of {steps.length}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => finish(true)}
                        className="rounded-full p-1 text-[#5E636E] hover:bg-zinc-100"
                        aria-label="Skip tour"
                        data-testid="guide-tour-skip"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <h3 className="mt-2.5 font-display text-lg font-semibold leading-tight" data-testid="guide-tour-title">
                    {step.title}
                </h3>
                <p className="mt-1.5 text-[13px] text-[#3F444C] leading-relaxed" data-testid="guide-tour-body">
                    {step.body}
                </p>

                <div className="mt-4 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setIdx((i) => Math.max(0, i - 1))}
                        disabled={isFirst}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#E4E4E7] px-2.5 py-1.5 text-xs font-medium text-[#3F444C] hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="guide-tour-back"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" /> Back
                    </button>
                    <button
                        type="button"
                        onClick={() => finish(true)}
                        className="ml-auto text-xs text-[#5E636E] hover:text-[#0F1115]"
                        data-testid="guide-tour-skip-link"
                    >
                        Skip tour
                    </button>
                    {isLast ? (
                        <button
                            type="button"
                            onClick={() => finish(true)}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                            data-testid="guide-tour-done"
                        >
                            Got it
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700"
                            data-testid="guide-tour-next"
                        >
                            Next <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});

export default GuideTour;
